import { graphql } from 'react-relay';

import { expValEquals } from '@confluence/feature-experiments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { ContentOperationsFragment } from './ContentOperationsFragment.graphql.ts';
import { SpaceOperationsFragment } from './SpaceOperationsFragment.graphql.ts';

void ContentOperationsFragment;
void SpaceOperationsFragment;

import type {
	entityOperationsHelpersSpaceOperationsQuery as entityOperationsHelpersSpaceOperationsQuery$data,
	entityOperationsHelpersSpaceOperationsQueryVariables as entityOperationsHelpersSpaceOperationsQuery$variables,
} from './__types__/entityOperationsHelpersSpaceOperationsQuery.ts';
import type {
	entityOperationsHelpersContentOperationsQuery as entityOperationsHelpersContentOperationsQuery$data,
	entityOperationsHelpersContentOperationsQueryVariables as entityOperationsHelpersContentOperationsQuery$variables,
} from './__types__/entityOperationsHelpersContentOperationsQuery.ts';
import type {
	ContentOperationsQuery as ContentOperationsQueryType,
	ContentOperationsQueryVariables,
	ContentOperationsQuery_content_PaginatedContentListWithChild_nodes_Content_operations_OperationCheckResult as ContentOperationsQuery_content_nodes_operations,
} from './__types__/ContentOperationsQuery.ts';
import { ContentOperationsQuery } from './ContentOperationsQuery.graphql.ts';
import type {
	canPerformContentOperationParams,
	canPerformSpaceOperationParams,
	checkContentOperationParams,
	checkSpaceOperationParams,
	UseCanPerformContentOperationType,
	UseCanPerformSpaceOperationType,
} from './operationsTypes.ts';
import type {
	SpaceOperationsQuery as SpaceOperationsQueryType,
	SpaceOperationsQueryVariables,
	SpaceOperationsQuery_space_Space_operations_OperationCheckResult as SpaceOperationsQuery_space_operations,
} from './__types__/SpaceOperationsQuery.ts';
import { SpaceOperationsQuery } from './SpaceOperationsQuery.graphql.ts';
/**
 * Validate `operation` on `contentType` given `operationCheckResult` from `ContentOperationsQuery`
 * If `contentType` is not specified, type of main/parent content in `operationCheckResult` will be assumed
 */
export const canPerformContentOperation = ({
	operationCheckResult,
	operation,
	contentType,
}: canPerformContentOperationParams): boolean => {
	const permittedOperations = (operationCheckResult?.content?.nodes?.[0]?.operations ??
		[]) as ContentOperationsQuery_content_nodes_operations[];
	const targetContentType = !!contentType
		? contentType
		: operationCheckResult?.content?.nodes?.[0]?.type;

	if (!targetContentType) return false;

	return permittedOperations.some(
		(permittedOperation) =>
			permittedOperation?.operation === operation &&
			permittedOperation?.targetType === targetContentType,
	);
};

/** Validate `operation` on `contentType` given `operationCheckResult` from `SpaceOperationsQuery */
export const canPerformSpaceOperation = ({
	operationCheckResult,
	operation,
	contentType,
}: canPerformSpaceOperationParams): boolean => {
	// prettier-ignore
	const permittedOperations = (operationCheckResult?.space?.operations ?? []) as SpaceOperationsQuery_space_operations[];

	return permittedOperations.some(
		(permittedOperation) =>
			permittedOperation?.operation === operation && permittedOperation?.targetType === contentType,
	);
};

/** Functions to query and check operation result */

/**
 * Retrieve operations for content with contentId, then check if user permitted to perform `operation` on target `contentType`
 * @param contentId id of target entity
 * @param operation operation type
 * @param contentType if empty, then content type of content with id contentId will be used
 */
export const checkOperationForContent = async ({
	contentId,
	operation,
	contentType,
}: checkContentOperationParams): Promise<boolean> => {
	const contentOperationsQueryResult = await queryInterop<
		ContentOperationsQueryType,
		ContentOperationsQueryVariables
	>(
		expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
			? {
					query: ContentOperationsQuery,
					variables: {
						contentId,
						status: ['current', 'archived', 'draft'],
					},
					fetchPolicy: 'cache-first' as const,
				}
			: {
					query: ContentOperationsQuery,
					variables: {
						contentId,
					},
				},
	);
	return canPerformContentOperation({
		operationCheckResult: contentOperationsQueryResult.data,
		operation,
		contentType,
	});
};

/**
 * Retrieve operations space, then check if user permitted to perform `operation` on target `contentType` within space
 */
export const checkOperationForSpace = async ({
	spaceKey,
	operation,
	contentType,
}: checkSpaceOperationParams): Promise<boolean> => {
	const spaceOperationsQueryResult = await queryInterop<
		SpaceOperationsQueryType,
		SpaceOperationsQueryVariables
	>({
		query: SpaceOperationsQuery,
		variables: {
			spaceKey,
		},
		...(expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
			? { fetchPolicy: 'cache-first' as const }
			: {}),
	});
	return canPerformSpaceOperation({
		operationCheckResult: spaceOperationsQueryResult.data,
		operation,
		contentType,
	});
};

type UseCanPerformContentOperationProps = checkContentOperationParams & {
	status?: (string | null)[] | null;
};

/**
 * A hook that checks if the current user can perform the specified `operation` on the content with the given ID and type.
 *
 * Returns `undefined` when the permission is loading,
 * and then `true` if the user can perform the operation, and `false` otherwise.
 */
export const useCanPerformContentOperation = ({
	contentId,
	operation,
	contentType,
	status,
}: UseCanPerformContentOperationProps): UseCanPerformContentOperationType => {
	const graphQLResult = useQueryInterop<
		entityOperationsHelpersContentOperationsQuery$data,
		entityOperationsHelpersContentOperationsQuery$variables
	>(
		graphql`
			query entityOperationsHelpersContentOperationsQuery($contentId: ID!, $status: [String]) {
				content(id: $contentId, status: $status) {
					nodes {
						...ContentOperationsFragment @relay(mask: false)
					}
				}
			}
		`,
		expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
			? {
					variables: {
						contentId,
						status: status ?? ['current', 'archived', 'draft'],
					},
					skip: !contentId,
					fetchPolicy: 'cache-first' as const,
				}
			: {
					variables: {
						contentId,
						status,
					},
					skip: !contentId,
				},
	);

	if (!contentId) {
		return false;
	}

	if (graphQLResult?.data) {
		return canPerformContentOperation({
			operationCheckResult: graphQLResult?.data,
			operation,
			contentType,
		});
	}

	return undefined;
};

/**
 * A hook that checks if the current user can perform the specified `operation` in the given space on the specified `contentType`.
 *
 * Returns `undefined` when the permission is loading,
 * and then `true` if the user can perform the operation, and `false` otherwise.
 */
export const useCanPerformSpaceOperation = ({
	spaceKey,
	operation,
	contentType,
	skip,
}: checkSpaceOperationParams): UseCanPerformSpaceOperationType => {
	const graphQLResult = useQueryInterop<
		entityOperationsHelpersSpaceOperationsQuery$data,
		entityOperationsHelpersSpaceOperationsQuery$variables
	>(
		graphql`
			query entityOperationsHelpersSpaceOperationsQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					...SpaceOperationsFragment @relay(mask: false)
				}
			}
		`,
		{
			variables: { spaceKey },
			skip,
			...(expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
				? { fetchPolicy: 'cache-first' as const }
				: {}),
		},
	);
	// Only show the publish button if the user has permission to update (edit) this content.
	let canPerformOperation = undefined;
	if (graphQLResult?.data) {
		canPerformOperation = canPerformSpaceOperation({
			operationCheckResult: graphQLResult?.data,
			operation,
			contentType,
		});
	}

	return [canPerformOperation, graphQLResult];
};
